// Two video elements that hand off to each other, so the home page's preview chip never has
// a moment without a picture.
//
// ─── WHY TWO ──────────────────────────────────────────────────────────────────────────────
//
// One <video> cannot present clip A while loading clip B. `load()` runs the media load
// algorithm, which drops readyState to HAVE_NOTHING and discards the current frame — so the
// element paints nothing for as long as the next source takes to arrive and decode. Measured
// on these files: ~180ms to first byte going direct to storage, plus the GOP, plus ~100ms of
// decode. A few hundred milliseconds with no picture, on every cycle.
//
// Four things were tried against that and all four were the same mistake — covering the gap
// rather than removing it. A start offset changed WHICH frame appeared. A direct storage url
// made the gap shorter. A poster covered the cold start. A canvas holding the outgoing frame
// turned the black into a freeze, which reads as buffering and was reported as worse.
//
// The gap is a property of the element, so the fix is a second element. The incoming clip is
// loaded, seeked and decoded OUT OF SIGHT, and only revealed once it actually has a frame.
// There is no moment to cover because there is no moment without a picture.
//
// ─── WHAT FALLS OUT OF IT ─────────────────────────────────────────────────────────────────
//
// The failure mode stops being a failure. If the next clip is not ready, the current one
// simply keeps playing and the hand-off happens later: a slow network degrades to "that
// lesson lingered a bit" instead of a black box. There is no safety timer to get wrong,
// because there is nothing to fall back FROM.
//
// It also deletes more than it adds — the held-frame canvas, its ref, its timer, the
// per-swap drawImage, the tainted-canvas caveat, and a React-managed style being written
// imperatively behind React's back.
//
// See PREVIEW_CHIP_PLAN.md for the options that were weighed and rejected.
import { useCallback, useEffect, useRef, useState } from "react";

/** How long the dissolve between two clips takes. Both sides hold a real frame throughout,
 *  so this is a crossfade and not a cover for anything — short enough to read as a cut with
 *  polish, long enough not to flicker. The one number to change if the feel is wrong. */
export const REEL_CROSSFADE_MS = 220;

/** A source that never becomes playable must not hold the reel forever. Generous: this is a
 *  ceiling on pathology, not a timeout anyone should reach. */
const PRIME_TIMEOUT_MS = 6000;

/** Resolve when `el` has actually got a frame for `src`.
 *
 *  TWO SIGNALS, BECAUSE NEITHER IS BOTH EXACT AND UNIVERSAL. `requestVideoFrameCallback`
 *  fires when a frame has been presented for compositing, which is precisely the question
 *  being asked — but Firefox does not implement it. `loadeddata` fires at HAVE_CURRENT_DATA,
 *  which guarantees a frame EXISTS without saying it has been painted; that is the universal
 *  floor and it is sufficient, because the element is revealed by an opacity transition that
 *  takes longer than a paint.
 *
 *  Deliberately NOT `canplay`: it fires at HAVE_FUTURE_DATA, which is about having enough
 *  data to continue, not about having a picture to show.
 */
const primeInto = (el, src) =>
  new Promise((resolve) => {
    let settled = false;
    const done = (ok) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      el.removeEventListener("loadeddata", onData);
      el.removeEventListener("error", onError);
      resolve(ok);
    };
    const onData = () => done(true);
    const onError = () => done(false);
    const timer = setTimeout(() => done(false), PRIME_TIMEOUT_MS);

    el.addEventListener("loadeddata", onData);
    el.addEventListener("error", onError);

    el.src = src;
    el.load();
    // The precise signal where it exists. Registered after load() so it belongs to this
    // source; harmless alongside `loadeddata` because `done` is idempotent.
    el.requestVideoFrameCallback?.(() => done(true));
  });

/**
 * @param {object}   o
 * @param {Array}    o.playlist  video records; the one matching `currentId` is looked up here
 * @param {string}   o.currentId which demo should be on screen
 * @param {string}   o.nextId    which demo to warm while `currentId` plays
 * @param {function} o.srcFor    (item, id) => url
 * @param {boolean}  o.muted
 * @param {boolean}  o.paused    composer expanded, inline player open, page hidden…
 * @param {boolean}  o.idle      the page is not on screen at all — release what we can
 * @param {function} o.onReady   called when the visible clip has a frame
 */
export const useDemoReel = ({
  playlist,
  currentId,
  nextId,
  srcFor,
  muted = true,
  paused = false,
  idle = false,
  onReady,
}) => {
  const aRef = useRef(null);
  const bRef = useRef(null);
  // The ONLY render-visible state. Opacity derives from it, so "both visible" and "neither
  // visible" are not representable.
  const [frontIsA, setFrontIsA] = useState(true);

  /* REFS FOR EVERYTHING THE ASYNC WORK READS. A prime resolves hundreds of milliseconds after
     it was started; reading React state there would read the state of a render that is long
     gone. These are the live values. */
  const frontIsARef = useRef(true);
  const playlistRef = useRef(playlist);
  const srcForRef = useRef(srcFor);
  const mutedRef = useRef(muted);
  const pausedRef = useRef(paused);
  const idleRef = useRef(idle);
  const onReadyRef = useRef(onReady);
  useEffect(() => { playlistRef.current = playlist; }, [playlist]);
  useEffect(() => { srcForRef.current = srcFor; }, [srcFor]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { pausedRef.current = paused; }, [paused]);
  useEffect(() => { idleRef.current = idle; }, [idle]);
  useEffect(() => { onReadyRef.current = onReady; }, [onReady]);

  /* WHAT EACH ELEMENT HOLDS, by id. The elements themselves cannot be asked — `el.src` is an
     absolute url with a media fragment, and comparing those is a parsing problem nobody
     should have. */
  const holdsRef = useRef({ a: null, b: null });
  /* SUPERSESSION. Every request takes a token; a prime that resolves holding a stale one is
     dropped. Without it, cycling faster than a clip can load (or a card click landing mid
     hand-off) races two reveals and can leave the roles inverted. */
  const tokenRef = useRef(0);
  /* ONE PRIME AT A TIME, AND THIS IS NOT BELT-AND-BRACES — it is load bearing.
     `primeInto` resolves on the element's next `loadeddata`, and an element has exactly one.
     Point the same element at a second source while a prime is awaiting and the FIRST
     promise resolves against the SECOND source's data, so the reveal shows a clip the page
     never asked for. The way in was ordinary: on a cold start the lookahead timer fires
     ~270ms into a ~400ms first load and retargets the very element being loaded, and the
     chip comes up on lesson two while the typewriter spells lesson one. */
  const inFlightRef = useRef(false);

  const els = useCallback(() => {
    const a = aRef.current;
    const b = bRef.current;
    const isA = frontIsARef.current;
    return { a, b, front: isA ? a : b, back: isA ? b : a, frontKey: isA ? "a" : "b",
             backKey: isA ? "b" : "a" };
  }, []);

  const lookup = useCallback(
    (id) => playlistRef.current?.find((v) => (v.job_id || v.video_id) === id) || null,
    [],
  );

  /** Load `id` into whichever element is NOT on screen. Resolves true when it has a frame.
   *  `force` is for the clip the page is actually waiting on; a lookahead never preempts a
   *  prime already running (see `inFlightRef`). */
  const primeBack = useCallback(async (id, { force = false } = {}) => {
    const { back, backKey } = els();
    if (!back || !id) return false;
    if (holdsRef.current[backKey] === id && back.readyState >= 2) return true;
    if (inFlightRef.current && !force) return false;

    const src = srcForRef.current?.(lookup(id), id);
    if (!src) return false;

    inFlightRef.current = true;
    holdsRef.current[backKey] = id;
    back.muted = true;          // a buffer that is not on screen is never audible
    try {
      const ok = await primeInto(back, src);
      // Hold it on its first frame. `load()` with a `#t=` fragment seeks as part of loading,
      // so by the time this resolves the element is parked on the frame it will show.
      back.pause();
      if (!ok) holdsRef.current[backKey] = null;
      return ok;
    } finally {
      inFlightRef.current = false;
    }
  }, [els, lookup]);

  /** Put the back element on screen. Both sides hold a frame, so this is a true crossfade. */
  const reveal = useCallback(() => {
    const { front, back } = els();
    if (!back) return;
    back.muted = mutedRef.current;
    if (!pausedRef.current) back.play().catch(() => {});
    frontIsARef.current = !frontIsARef.current;
    setFrontIsA(frontIsARef.current);
    onReadyRef.current?.();
    // Quiet the outgoing one only once it is invisible, so nothing is seen to stop.
    const stale = front;
    setTimeout(() => { stale?.pause(); }, REEL_CROSSFADE_MS);
  }, [els]);

  /* ── WHAT IS ON SCREEN ──────────────────────────────────────────────────────────────────
   *
   *  The whole policy, in one effect:
   *    already on screen      → nothing to do
   *    already warmed in back → reveal immediately
   *    otherwise              → warm it, KEEP PLAYING what is there, reveal when it has a
   *                             frame. This branch is the one that used to be black.
   */
  useEffect(() => {
    if (!currentId) return undefined;
    const token = ++tokenRef.current;
    let cancelled = false;

    (async () => {
      const { front, frontKey } = els();
      if (holdsRef.current[frontKey] === currentId) {
        if (front && front.readyState >= 2) onReadyRef.current?.();
        return;
      }
      const ok = await primeBack(currentId, { force: true });
      if (cancelled || token !== tokenRef.current) return;
      if (ok) reveal();
    })();

    return () => { cancelled = true; };
  }, [currentId, els, primeBack, reveal]);

  /* ── WARM THE NEXT ONE ───────────────────────────────────────────────────────────────────
   *  Runs after a hand-off has settled, against the element that just went dark. ~4.5s of
   *  lead time per cycle against a ~400ms load, which is why the reveal above is almost
   *  always the instant branch.
   *
   *  THREE GUARDS, ALL AGAINST THE SAME MISTAKE: warming the next clip must never disturb
   *  the one the page is waiting on. The token drops a lookahead whose request has been
   *  superseded; the `holds` check skips it while a hand-off has not landed yet; and
   *  `primeBack` itself refuses to start without `force` while a prime is running. */
  useEffect(() => {
    if (!nextId || nextId === currentId) return undefined;
    const token = tokenRef.current;
    const id = window.setTimeout(() => {
      if (token !== tokenRef.current || idleRef.current) return;
      const { frontKey } = els();
      if (holdsRef.current[frontKey] !== currentId) return;   // hand-off still pending
      primeBack(nextId);
    }, REEL_CROSSFADE_MS + 50);
    return () => window.clearTimeout(id);
    /* `frontIsA` IS A DEPENDENCY WITHOUT BEING READ, deliberately: it is how this re-arms
       after a hand-off lands. On a cold start the first attempt fires while the opening
       clip is still loading and the `holds` guard correctly declines it — and without a
       re-run on the role swap it would never try again, leaving the very first transition
       the one unwarmed one. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextId, currentId, frontIsA, primeBack, els]);

  // Mute applies to BOTH: the back buffer must never be able to make a sound when revealed.
  useEffect(() => {
    const { a, b, front } = els();
    if (a) a.muted = true;
    if (b) b.muted = true;
    if (front) front.muted = muted;
  }, [muted, frontIsA, els]);

  // Pause both; resume only the one on screen.
  useEffect(() => {
    const { front, back } = els();
    if (paused) {
      front?.pause();
      back?.pause();
    } else {
      front?.play().catch(() => {});
    }
  }, [paused, frontIsA, els]);

  /* ── OFF THE PAGE MEANS LET GO ───────────────────────────────────────────────────────
   *
   *  HomePage is never unmounted — it is hidden with `visibility:hidden; height:0` and
   *  left in the tree — so without this the reel keeps TWO `preload="auto"` elements
   *  holding a decoder and an open stream each, for a page nobody is looking at, for as
   *  long as the session lasts. Pausing is not enough: it stops playback, not the element
   *  being a 5MB download with a decoder attached.
   *
   *  The BACK one is released outright — it is invisible by definition, so there is
   *  nothing to preserve. The FRONT one keeps its frame, which is what makes coming back
   *  to Home instant rather than a cold start; paused, the browser stops buffering it of
   *  its own accord.
   *
   *  Clearing `holds` is what makes this safe: the lookahead re-primes from scratch when
   *  the page returns instead of trusting a buffer emptied underneath it.
   *  `removeAttribute` then `load()` is the only way to actually abort a media download —
   *  changing `preload` after the fact does nothing to a fetch already running. */
  useEffect(() => {
    if (!idle) return;
    const { back, backKey } = els();
    if (!back) return;
    back.pause();
    back.removeAttribute("src");
    back.load();
    holdsRef.current[backKey] = null;
  }, [idle, frontIsA, els]);

  return { aRef, bRef, frontIsA };
};

export default useDemoReel;
